import { memo, useState } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { MessageSquare, Zap, Tag, ArrowRightLeft, CircleDot, Square, Clock, Variable, Users, FileText, Pencil, HelpCircle, Handshake, Bot } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AutomationTagSelector } from "./AutomationTagSelector";
import { AutomationFunnelStageSelector } from "./AutomationFunnelStageSelector";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/* ── Style map with proper light/dark contrast ── */
const nodeStyles: Record<string, {
  icon: typeof Zap;
  label: string;
  light: string;
  dark: string;
}> = {
  trigger:      { icon: Zap,            label: "Gatilho",         light: "bg-amber-50 border-amber-300 text-amber-900",         dark: "dark:bg-amber-950 dark:border-amber-700 dark:text-amber-100" },
  condition:    { icon: HelpCircle,     label: "Condição",        light: "bg-blue-50 border-blue-300 text-blue-900",            dark: "dark:bg-blue-950 dark:border-blue-700 dark:text-blue-100" },
  send_message: { icon: MessageSquare,  label: "Mensagem",        light: "bg-green-50 border-green-300 text-green-900",         dark: "dark:bg-green-950 dark:border-green-700 dark:text-green-100" },
  add_tag:      { icon: Tag,            label: "Add Tag",         light: "bg-purple-50 border-purple-300 text-purple-900",      dark: "dark:bg-purple-950 dark:border-purple-700 dark:text-purple-100" },
  remove_tag:   { icon: Tag,            label: "Remove Tag",      light: "bg-red-50 border-red-300 text-red-900",               dark: "dark:bg-red-950 dark:border-red-700 dark:text-red-100" },
  move_funnel:  { icon: ArrowRightLeft, label: "Mover Funil",     light: "bg-indigo-50 border-indigo-300 text-indigo-900",      dark: "dark:bg-indigo-950 dark:border-indigo-700 dark:text-indigo-100" },
  move_column:  { icon: ArrowRightLeft, label: "Mover Coluna",    light: "bg-cyan-50 border-cyan-300 text-cyan-900",            dark: "dark:bg-cyan-950 dark:border-cyan-700 dark:text-cyan-100" },
  add_comment:  { icon: FileText,       label: "Comentário",      light: "bg-yellow-50 border-yellow-300 text-yellow-900",      dark: "dark:bg-yellow-950 dark:border-yellow-700 dark:text-yellow-100" },
  create_task:  { icon: CircleDot,      label: "Criar Tarefa",    light: "bg-teal-50 border-teal-300 text-teal-900",            dark: "dark:bg-teal-950 dark:border-teal-700 dark:text-teal-100" },
  update_lead:  { icon: Pencil,         label: "Atualizar Lead",  light: "bg-orange-50 border-orange-300 text-orange-900",      dark: "dark:bg-orange-950 dark:border-orange-700 dark:text-orange-100" },
  create_deal:  { icon: Handshake,      label: "Negociação",      light: "bg-emerald-50 border-emerald-300 text-emerald-900",   dark: "dark:bg-emerald-950 dark:border-emerald-700 dark:text-emerald-100" },
  set_ai_agent: { icon: Bot,            label: "Agente IA",       light: "bg-violet-50 border-violet-300 text-violet-900",      dark: "dark:bg-violet-950 dark:border-violet-700 dark:text-violet-100" },
  delay:        { icon: Clock,          label: "Delay",           light: "bg-slate-50 border-slate-300 text-slate-900",          dark: "dark:bg-slate-950 dark:border-slate-700 dark:text-slate-100" },
  set_variable: { icon: Variable,       label: "Variável",        light: "bg-pink-50 border-pink-300 text-pink-900",            dark: "dark:bg-pink-950 dark:border-pink-700 dark:text-pink-100" },
  end:          { icon: Square,         label: "Fim",             light: "bg-red-50 border-red-300 text-red-900",               dark: "dark:bg-red-950 dark:border-red-700 dark:text-red-100" },
};

/* ── Trigger keywords: raw text, parse only on blur/save ── */
function TriggerKeywordsField({ config, onChange }: { config: Record<string, unknown>; onChange: (key: string, value: unknown) => void }) {
  const initial = Array.isArray(config.keywords) ? (config.keywords as string[]).join(", ") : String(config.keywords || "");
  const [raw, setRaw] = useState(initial);

  const commitKeywords = (text: string) => {
    const parsed = text.split(",").map(s => s.trim()).filter(Boolean);
    onChange("keywords", parsed);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>Palavras-chave (separadas por vírgula)</Label>
        <Input
          value={raw}
          onChange={e => setRaw(e.target.value)}
          onBlur={() => commitKeywords(raw)}
          placeholder="oi, olá, bom dia"
        />
        {raw && (
          <div className="flex flex-wrap gap-1 mt-2">
            {raw.split(",").map(s => s.trim()).filter(Boolean).map((kw, i) => (
              <span key={i} className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">{kw}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Config dialog fields per node type ── */
function NodeConfigFields({ nodeType, config, onChange, workspaceId }: {
  nodeType: string;
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  workspaceId?: string;
}) {
  switch (nodeType) {
    case "trigger": {
      const triggerType = String(config.trigger_type || "");
      if (triggerType === "lead_stage_entered") {
        return workspaceId ? (
          <AutomationFunnelStageSelector
            workspaceId={workspaceId}
            selectedFunnelId={config.funnel_id as string | undefined}
            selectedFunnelName={config.funnel_name as string | undefined}
            selectedStageId={config.stage_id as string | undefined}
            selectedStageName={config.stage_name as string | undefined}
            onSelect={(data) => {
              onChange("funnel_id", data.funnel_id);
              onChange("funnel_name", data.funnel_name);
              onChange("stage_id", data.stage_id);
              onChange("stage_name", data.stage_name);
              onChange("stage", data.stage_slug);
              onChange("stage_slug", data.stage_slug);
            }}
          />
        ) : <p className="text-sm text-muted-foreground">Carregando workspace...</p>;
      }
      if (triggerType === "tag_added") {
        return workspaceId ? (
          <AutomationTagSelector
            workspaceId={workspaceId}
            selectedTagId={config.tag_id as string | undefined}
            selectedTagName={config.tag_name as string | undefined}
            onSelect={(tag) => {
              onChange("tag_id", tag.id);
              onChange("tag_name", tag.name);
            }}
          />
        ) : <p className="text-sm text-muted-foreground">Carregando workspace...</p>;
      }
      return <TriggerKeywordsField config={config} onChange={onChange} />;
    }
    case "send_message":
      return (
        <div className="space-y-3">
          <div>
            <Label>Canal de envio</Label>
            <Select
              value={String(config.delivery_channel || "inbound")}
              onValueChange={(v) => onChange("delivery_channel", v === "inbound" ? "" : v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inbound">Canal do gatilho</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="messenger">Messenger</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Mensagem</Label>
            <Textarea
              value={String(config.message || "")}
              onChange={e => onChange("message", e.target.value)}
              placeholder="Digite a mensagem..."
              rows={4}
            />
          </div>
        </div>
      );
    case "add_tag":
    case "remove_tag":
      return (
        <div className="space-y-3">
          <div>
            <Label>{nodeType === "add_tag" ? "Selecione a tag para adicionar" : "Selecione a tag para remover"}</Label>
            {workspaceId ? (
              <AutomationTagSelector
                workspaceId={workspaceId}
                selectedTagId={config.tag_id as string | undefined}
                selectedTagName={config.tag_name as string | undefined}
                onSelect={(tag) => {
                  onChange("tag_id", tag.id);
                  onChange("tag_name", tag.name);
                  onChange("tag_color", tag.color);
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Carregando workspace...</p>
            )}
          </div>
        </div>
      );
    case "delay":
      return (
        <div className="space-y-3">
          <div>
            <Label>Minutos de espera</Label>
            <Input
              type="number"
              min={1}
              value={String(config.minutes || "")}
              onChange={e => onChange("minutes", Number(e.target.value))}
              placeholder="5"
            />
            <p className="text-xs text-muted-foreground mt-1">
              O fluxo ainda executa sem pausa real; o delay será aplicado em versão futura.
            </p>
          </div>
        </div>
      );
    case "condition": {
      const condType = String(config.condition_type || "");
      return (
        <div className="space-y-3">
          <div>
            <Label>Tipo de condição</Label>
            <Select value={condType} onValueChange={v => { onChange("condition_type", v); onChange("condition_value", ""); }}>
              <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="has_tag">Possui tag</SelectItem>
                <SelectItem value="in_funnel">Está no funil</SelectItem>
                <SelectItem value="in_stage">Está na etapa</SelectItem>
                <SelectItem value="message_contains">Mensagem contém</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {condType === "has_tag" && workspaceId && (
            <AutomationTagSelector
              workspaceId={workspaceId}
              selectedTagId={config.condition_tag_id as string | undefined}
              selectedTagName={config.condition_value as string | undefined}
              onSelect={(tag) => {
                onChange("condition_tag_id", tag.id);
                onChange("condition_value", tag.name);
              }}
            />
          )}
          {(condType === "in_funnel" || condType === "in_stage") && workspaceId && (
            <AutomationFunnelStageSelector
              workspaceId={workspaceId}
              selectedFunnelId={config.condition_funnel_id as string | undefined}
              selectedFunnelName={config.condition_funnel_name as string | undefined}
              selectedStageId={config.condition_stage_id as string | undefined}
              selectedStageName={config.condition_stage_name as string | undefined}
              onSelect={(data) => {
                onChange("condition_funnel_id", data.funnel_id);
                onChange("condition_funnel_name", data.funnel_name);
                onChange("condition_stage_id", data.stage_id);
                onChange("condition_stage_name", data.stage_name);
                onChange("condition_stage_slug", data.stage_slug);
                onChange("condition_value", condType === "in_funnel" ? data.funnel_name : data.stage_name);
              }}
            />
          )}
          {condType === "message_contains" && (
            <div>
              <Label>Texto</Label>
              <Input
                value={String(config.condition_value || "")}
                onChange={e => onChange("condition_value", e.target.value)}
                placeholder="ex: promoção, desconto"
              />
            </div>
          )}
        </div>
      );
    }
    case "move_funnel":
      return (
        <div className="space-y-3">
          {workspaceId ? (
            <AutomationFunnelStageSelector
              workspaceId={workspaceId}
              selectedFunnelId={config.funnel_id as string | undefined}
              selectedFunnelName={config.funnel_name as string | undefined}
              selectedStageId={config.stage_id as string | undefined}
              selectedStageName={config.stage_name as string | undefined}
              showFunnelLabel="Funil de destino"
              showStageLabel="Etapa de destino"
              onSelect={(data) => {
                onChange("funnel_id", data.funnel_id);
                onChange("funnel_name", data.funnel_name);
                onChange("stage_id", data.stage_id);
                onChange("stage_name", data.stage_name);
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Carregando workspace...</p>
          )}
        </div>
      );
    case "move_column":
      return (
        <div className="space-y-3">
          {workspaceId ? (
            <AutomationFunnelStageSelector
              workspaceId={workspaceId}
              selectedFunnelId={config.funnel_id as string | undefined}
              selectedFunnelName={config.funnel_name as string | undefined}
              selectedStageId={config.column_id as string | undefined}
              selectedStageName={config.column_name as string | undefined}
              showFunnelLabel="Funil"
              showStageLabel="Coluna de destino"
              onSelect={(data) => {
                onChange("funnel_id", data.funnel_id);
                onChange("funnel_name", data.funnel_name);
                onChange("column_id", data.stage_id);
                onChange("column_name", data.stage_name);
                onChange("stage", data.stage_slug);
                onChange("stage_slug", data.stage_slug);
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Carregando workspace...</p>
          )}
        </div>
      );
    case "add_comment":
      return (
        <div className="space-y-3">
          <div>
            <Label>Comentário</Label>
            <Textarea
              value={String(config.comment || "")}
              onChange={e => onChange("comment", e.target.value)}
              placeholder="Comentário interno..."
              rows={3}
            />
          </div>
        </div>
      );
    case "create_task":
      return (
        <div className="space-y-3">
          <div>
            <Label>Título da Tarefa</Label>
            <Input
              value={String(config.title || config.task_title || "")}
              onChange={e => onChange("title", e.target.value)}
              placeholder="ex: Fazer follow-up"
            />
          </div>
        </div>
      );
    case "update_lead":
      return (
        <div className="space-y-3">
          <div>
            <Label>Campo</Label>
            <Input
              value={String(config.field || (config.fields && typeof config.fields === "object" ? Object.keys(config.fields as object)[0] : "") || "")}
              onChange={e => {
                const fieldName = e.target.value;
                const currentValue = String(config.value ?? (config.fields as Record<string, unknown>)?.[fieldName] ?? "");
                onChange("field", fieldName);
                onChange("fields", fieldName ? { [fieldName]: currentValue } : {});
              }}
              placeholder="ex: email, phone"
            />
          </div>
          <div>
            <Label>Valor</Label>
            <Input
              value={String(config.value ?? (config.fields as Record<string, unknown>)?.[String(config.field || "")] ?? "")}
              onChange={e => {
                const fieldName = String(config.field || "");
                onChange("value", e.target.value);
                if (fieldName) onChange("fields", { [fieldName]: e.target.value });
              }}
              placeholder="Novo valor"
            />
          </div>
        </div>
      );
    case "create_deal":
      return (
        <div className="space-y-3">
          <div>
            <Label>Título da negociação</Label>
            <Input
              value={String(config.title || "")}
              onChange={e => onChange("title", e.target.value)}
              placeholder="Negociação"
            />
          </div>
          <div>
            <Label>Valor (R$)</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={String(config.manual_amount || "")}
              onChange={e => onChange("manual_amount", e.target.value)}
              placeholder="0,00"
            />
          </div>
        </div>
      );
    case "set_ai_agent":
      return (
        <div className="space-y-3">
          <Label>Ação no agente</Label>
          <Select value={String(config.mode || "pause")} onValueChange={v => onChange("mode", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pause">Pausar agente</SelectItem>
              <SelectItem value="resume">Reativar agente</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    case "set_variable":
      return (
        <div className="space-y-3">
          <div>
            <Label>Nome da Variável</Label>
            <Input
              value={String(config.variable_name || config.var_name || "")}
              onChange={e => onChange("variable_name", e.target.value)}
              placeholder="ex: score"
            />
          </div>
          <div>
            <Label>Valor</Label>
            <Input
              value={String(config.variable_value || config.var_value || "")}
              onChange={e => onChange("variable_value", e.target.value)}
              placeholder="ex: 10"
            />
          </div>
        </div>
      );
    default:
      return <p className="text-sm text-muted-foreground">Este bloco não possui configurações editáveis.</p>;
  }
}

/* ── Node component ── */
function AutomationNode({ data, type, id }: NodeProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const nodeType = (type as string) || "condition";
  const style = nodeStyles[nodeType] || nodeStyles.condition;
  const Icon = style.icon;
  const dataRecord = data as Record<string, unknown>;
  const nodeLabel = (typeof dataRecord?.label === "string" ? dataRecord.label : "") || style.label;
  const config = (dataRecord?.config as Record<string, unknown>) || {};
  const onConfigChange = dataRecord?.onConfigChange as ((nodeId: string, newConfig: Record<string, unknown>) => void) | undefined;
  const workspaceId = dataRecord?.workspaceId as string | undefined;

  const isSource = nodeType !== "end";
  const isTarget = nodeType !== "trigger";

  const [localConfig, setLocalConfig] = useState<Record<string, unknown>>(config);

  const handleOpen = () => {
    setLocalConfig({ ...config });
    setDialogOpen(true);
  };

  const handleFieldChange = (key: string, value: unknown) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onConfigChange?.(id, localConfig);
    setDialogOpen(false);
  };

  return (
    <>
      <div
        onClick={handleOpen}
        className={cn(
          "rounded-lg border-2 px-4 py-3 min-w-[180px] max-w-[240px] shadow-sm cursor-pointer transition-shadow hover:shadow-md",
          style.light,
          style.dark,
        )}
      >
        {isTarget && (
          <Handle type="target" position={Position.Top} className="!bg-primary !w-3 !h-3 !border-2 !border-background" />
        )}

        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 flex-shrink-0 opacity-70" />
          <span className="text-sm font-medium truncate">{nodeLabel}</span>
        </div>

        {/* Brief config preview */}
        {Object.keys(config).length > 0 && (
          <div className="mt-1.5 text-xs opacity-60 truncate">
            {nodeType === "send_message" && config.message && (
              <span>{String(config.message).substring(0, 40)}...</span>
            )}
            {nodeType === "delay" && config.minutes && (
              <span>{String(config.minutes)} min</span>
            )}
            {nodeType === "trigger" && config.keywords && (
              <span>{(config.keywords as string[]).join(", ")}</span>
            )}
            {(nodeType === "add_tag" || nodeType === "remove_tag") && config.tag_name && (
              <span>Tag: {String(config.tag_name)}</span>
            )}
            {nodeType === "move_funnel" && config.funnel_name && (
              <span>Funil: {String(config.funnel_name)}{config.stage_name ? ` → ${String(config.stage_name)}` : ""}</span>
            )}
            {nodeType === "move_column" && config.column_name && (
              <span>Etapa: {String(config.column_name)}</span>
            )}
            {nodeType === "condition" && config.condition_type && (
              <span>{String(config.condition_type)}: {String(config.condition_value || "...")}</span>
            )}
          </div>
        )}

        {isSource && (
          <Handle type="source" position={Position.Bottom} className="!bg-primary !w-3 !h-3 !border-2 !border-background" />
        )}
      </div>

      {/* Config Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" onClick={e => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="h-5 w-5" />
              Configurar: {nodeLabel}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <NodeConfigFields
              nodeType={nodeType}
              config={localConfig}
              onChange={handleFieldChange}
              workspaceId={workspaceId}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const automationNodeTypes = {
  trigger: memo(AutomationNode),
  condition: memo(AutomationNode),
  send_message: memo(AutomationNode),
  add_tag: memo(AutomationNode),
  remove_tag: memo(AutomationNode),
  move_funnel: memo(AutomationNode),
  move_column: memo(AutomationNode),
  add_comment: memo(AutomationNode),
  create_task: memo(AutomationNode),
  update_lead: memo(AutomationNode),
  delay: memo(AutomationNode),
  set_variable: memo(AutomationNode),
  create_deal: memo(AutomationNode),
  set_ai_agent: memo(AutomationNode),
  end: memo(AutomationNode),
};
